/**
 * MemberActionConfirmSheet — right-anchored confirm sheet for CRM actions.
 * Matches s4-action-menu variant-A (single member) and variant-C (bulk composer).
 */

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import { X, Send, UserPlus, PenLine, LayoutGrid } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { useClubAdmin } from '@/hooks/useClubAdmin';
import { useAppTranslations } from '@/hooks/useAppTranslations';
import { staffMemberMessagingService } from '@/services/api/staff-member-messaging.service';
import { memberActionLogService } from '@/services/domains/crm/member-action-log.service';
import type { MemberActionType } from './MemberActionMenu';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ActionSheetMember {
  id: string;
  name: string;
  avatarColor?: string;
  initials: string;
  coachName?: string;
  daysSinceLastSession?: number;
}

interface MemberActionConfirmSheetProps {
  actionType: MemberActionType | null;
  member: ActionSheetMember | null;
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// Re-engagement templates (labels only — bodies remain English content)
// ---------------------------------------------------------------------------

const RE_ENGAGEMENT_TEMPLATES = [
  {
    label: 'We miss you',
    subject: (name: string) => `${name}, we miss you on the course`,
    body: (name: string) =>
      `Hi ${name},\n\nNoticed it's been a few weeks since your last round. Your coach is running a clinic this Saturday at 10am — happy to save you a spot, no charge.\n\nLet me know if there's anything that would make getting back out easier.\n\n— The team`,
  },
  {
    label: 'Free 9 holes',
    subject: (name: string) => `${name} — complimentary 9 holes this weekend`,
    body: (name: string) =>
      `Hi ${name},\n\nWe'd love to see you back on the course. We're offering you a complimentary 9 holes this weekend — just reply to this message and we'll sort the booking.\n\nHope to see you soon!\n\n— The team`,
  },
  {
    label: 'Coach intro',
    subject: (name: string) => `${name} — meet your new coach`,
    body: (name: string) =>
      `Hi ${name},\n\nWe've matched you with one of our coaches who we think would be a great fit. They'd love to set up a short intro session — no obligation, just a chance to connect.\n\nWant us to make the introduction?\n\n— The team`,
  },
];

// ---------------------------------------------------------------------------
// Re-engagement sheet
// ---------------------------------------------------------------------------

const ReEngagementSheet: React.FC<{
  member: ActionSheetMember;
  onClose: () => void;
}> = ({ member, onClose }) => {
  const { t } = useAppTranslations();
  const { currentClub } = useClubAdmin();
  const { toast } = useToast();
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    const tpl = RE_ENGAGEMENT_TEMPLATES[selectedIdx];
    setSubject(tpl.subject(member.name.split(' ')[0]));
    setBody(tpl.body(member.name.split(' ')[0]));
  }, [selectedIdx, member.name]);

  const handleSend = async () => {
    if (!currentClub) return;
    setIsSending(true);
    try {
      await staffMemberMessagingService.sendMemberMessage({
        memberUserId: member.id,
        clubId: currentClub.id,
        content: `Subject: ${subject}\n\n${body}`,
      });
      await memberActionLogService.logAction({
        memberIds: [member.id],
        clubId: currentClub.id,
        actionType: 're_engagement',
        payload: { template: RE_ENGAGEMENT_TEMPLATES[selectedIdx].label },
      });
      toast({ title: t.crm.confirmSheets.messageSent, description: `${t.crm.confirmSheets.sentTo} ${member.name}` });
      onClose();
    } catch {
      toast({ title: t.crm.confirmSheets.failedToSend, variant: 'destructive' });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <>
      {/* Header */}
      <div className="px-6 py-5 border-b border-slate-100 flex-shrink-0" style={{ paddingTop: 'calc(1.25rem + env(safe-area-inset-top, 0px))', paddingBottom: '1.25rem' }}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{t.crm.confirmSheets.sendReEngagement}</p>
            <p className="text-[17px] font-bold text-slate-900 mt-1">{t.crm.confirmSheets.to} {member.name}</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors"
          >
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

        {/* Template picker */}
        <div>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">{t.crm.confirmSheets.templateLabel}</p>
          <div className="flex gap-2 flex-wrap">
            {RE_ENGAGEMENT_TEMPLATES.map((tpl, idx) => (
              <button
                key={tpl.label}
                onClick={() => { setSelectedIdx(idx); setIsEditing(false); }}
                className={`px-3 py-1.5 rounded-lg text-[12px] font-semibold border transition-colors ${
                  selectedIdx === idx
                    ? 'bg-green-50 text-green-700 border-green-300'
                    : 'bg-slate-50 text-slate-600 border-slate-200 hover:border-slate-300'
                }`}
              >
                {tpl.label}
              </button>
            ))}
          </div>
        </div>

        {/* Subject */}
        <div>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">{t.crm.confirmSheets.subjectLabel}</p>
          <input
            type="text"
            value={subject}
            onChange={e => setSubject(e.target.value)}
            className="w-full px-3 py-2 text-[13px] border border-slate-200 rounded-lg focus:outline-none focus:border-slate-400 text-slate-800"
          />
        </div>

        {/* Message body */}
        <div>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">{t.crm.confirmSheets.messageLabel}</p>
          {isEditing ? (
            <textarea
              value={body}
              onChange={e => setBody(e.target.value)}
              rows={8}
              className="w-full px-3 py-2.5 text-[13px] border border-slate-200 rounded-lg focus:outline-none focus:border-slate-400 text-slate-800 resize-none leading-relaxed"
            />
          ) : (
            <div
              className="px-4 py-3.5 bg-slate-50 rounded-xl text-[13px] text-slate-700 leading-relaxed whitespace-pre-line cursor-text"
              onClick={() => setIsEditing(true)}
            >
              {body}
            </div>
          )}
        </div>

        {/* Logged note */}
        <p className="text-[11px] text-slate-400 flex items-center gap-1.5">
          <span className="w-3.5 h-3.5 rounded-full bg-slate-200 flex items-center justify-center text-slate-500 flex-shrink-0 text-[8px] font-bold">i</span>
          {t.crm.confirmSheets.actionWillBeLogged} {member.name.split(' ')[0]}{t.crm.confirmSheets.sProfile}
        </p>
      </div>

      {/* Footer */}
      <div className="px-6 py-4 border-t border-slate-100 flex gap-2 flex-shrink-0">
        <Button
          variant="outline"
          className="flex-1 text-[13px]"
          onClick={() => setIsEditing(true)}
        >
          {t.crm.confirmSheets.editMessage}
        </Button>
        <Button
          className="flex-[1.4] text-[13px] gap-1.5"
          style={{ background: 'linear-gradient(135deg, #2A4494, #1E3270)' }}
          onClick={handleSend}
          disabled={isSending}
        >
          <Send className="w-3.5 h-3.5" />
          {isSending ? t.crm.confirmSheets.sending : t.crm.confirmSheets.sendNow}
        </Button>
      </div>
    </>
  );
};

// ---------------------------------------------------------------------------
// Log follow-up sheet
// ---------------------------------------------------------------------------

const LogFollowUpSheet: React.FC<{
  member: ActionSheetMember;
  onClose: () => void;
}> = ({ member, onClose }) => {
  const { t } = useAppTranslations();
  const { currentClub } = useClubAdmin();
  const { toast } = useToast();
  const [note, setNote] = useState('');

  const handleSave = async () => {
    if (!note.trim()) return;
    if (!currentClub) {
      toast({ title: t.crm.confirmSheets.error ?? 'Error', description: 'Club not loaded, please try again.' });
      return;
    }
    await memberActionLogService.logAction({
      memberIds: [member.id],
      clubId: currentClub.id,
      actionType: 'log_followup',
      payload: { note },
    });
    toast({ title: t.crm.confirmSheets.followUpLogged, description: `${t.crm.confirmSheets.savedFor} ${member.name}` });
    onClose();
  };

  return (
    <>
      <div className="px-6 py-5 border-b border-slate-100 flex-shrink-0" style={{ paddingTop: 'calc(1.25rem + env(safe-area-inset-top, 0px))', paddingBottom: '1.25rem' }}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{t.crm.confirmSheets.logFollowUp}</p>
            <p className="text-[17px] font-bold text-slate-900 mt-1">{t.crm.confirmSheets.to} {member.name}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">{t.crm.confirmSheets.noteLabel}</p>
          <textarea
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder="What did you do or discuss? E.g. 'Called, left voicemail. Will try again Thursday.'"
            rows={6}
            className="w-full px-3 py-2.5 text-[13px] border border-slate-200 rounded-lg focus:outline-none focus:border-slate-400 text-slate-800 resize-none leading-relaxed placeholder:text-slate-300"
          />
        </div>
        <p className="text-[11px] text-slate-400">{t.crm.confirmSheets.savedWithTimestamp} {member.name.split(' ')[0]}{t.crm.confirmSheets.sProfile}</p>
      </div>

      <div className="px-6 py-4 border-t border-slate-100 flex gap-2 flex-shrink-0">
        <Button variant="outline" className="flex-1 text-[13px]" onClick={onClose}>{t.crm.confirmSheets.cancel}</Button>
        <Button
          className="flex-[1.4] text-[13px] gap-1.5"
          style={{ background: 'linear-gradient(135deg, #1B4332, #2D5A3D)' }}
          onClick={handleSave}
          disabled={!note.trim()}
        >
          <PenLine className="w-3.5 h-3.5" />
          {t.crm.confirmSheets.saveNote}
        </Button>
      </div>
    </>
  );
};

// ---------------------------------------------------------------------------
// Reassign coach sheet (placeholder — needs real coach data)
// ---------------------------------------------------------------------------

const ReassignCoachSheet: React.FC<{
  member: ActionSheetMember;
  onClose: () => void;
}> = ({ member, onClose }) => {
  const { t } = useAppTranslations();
  const { toast } = useToast();

  return (
    <>
      <div className="px-6 py-5 border-b border-slate-100 flex-shrink-0" style={{ paddingTop: 'calc(1.25rem + env(safe-area-inset-top, 0px))', paddingBottom: '1.25rem' }}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{t.crm.confirmSheets.reassignCoach}</p>
            <p className="text-[17px] font-bold text-slate-900 mt-1">{t.crm.confirmSheets.to} {member.name}</p>
            {member.coachName && (
              <p className="text-[12px] text-slate-400 mt-1">
                {t.crm.confirmSheets.currently} <span className="font-semibold text-slate-600">{member.coachName}</span>
                {member.daysSinceLastSession && ` · ${member.daysSinceLastSession}${t.crm.confirmSheets.daysSinceLastSession}`}
              </p>
            )}
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-5">
        <p className="text-[11px] text-slate-400 text-center py-8">
          {t.crm.confirmSheets.coachPickerComingSoon}
        </p>
      </div>

      <div className="px-6 py-4 border-t border-slate-100 flex gap-2 flex-shrink-0">
        <Button variant="outline" className="flex-1 text-[13px]" onClick={onClose}>{t.crm.confirmSheets.cancel}</Button>
        <Button
          className="flex-[1.4] text-[13px] gap-1.5"
          style={{ background: 'linear-gradient(135deg, #2A4494, #1E3270)' }}
          onClick={() => { toast({ title: t.crm.confirmSheets.comingSoon }); onClose(); }}
        >
          <UserPlus className="w-3.5 h-3.5" />
          {t.crm.confirmSheets.confirmReassignment}
        </Button>
      </div>
    </>
  );
};

// ---------------------------------------------------------------------------
// Comp something sheet (stub)
// ---------------------------------------------------------------------------

const CompSomethingSheet: React.FC<{
  member: ActionSheetMember;
  onClose: () => void;
}> = ({ member, onClose }) => {
  const { t } = useAppTranslations();
  const { currentClub } = useClubAdmin();
  const { toast } = useToast();
  const options = ['Free 9 holes', 'Range tokens (30 min)', 'Group clinic invite'];
  const [selected, setSelected] = useState<string | null>(null);

  return (
    <>
      <div className="px-6 py-5 border-b border-slate-100 flex-shrink-0" style={{ paddingTop: 'calc(1.25rem + env(safe-area-inset-top, 0px))', paddingBottom: '1.25rem' }}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{t.crm.confirmSheets.compSomething}</p>
            <p className="text-[17px] font-bold text-slate-900 mt-1">{t.crm.confirmSheets.to} {member.name}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-3">
        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-3">{t.crm.confirmSheets.chooseWhatToOffer}</p>
        {options.map(opt => (
          <button
            key={opt}
            onClick={() => setSelected(opt)}
            className={`w-full text-left px-4 py-3.5 rounded-xl border text-[13px] font-medium transition-colors ${
              selected === opt
                ? 'bg-green-50 border-green-300 text-green-800'
                : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300'
            }`}
          >
            {opt}
          </button>
        ))}
        <p className="text-[11px] text-slate-400 pt-2">{t.crm.confirmSheets.voucherSystemComingSoon}</p>
      </div>

      <div className="px-6 py-4 border-t border-slate-100 flex gap-2 flex-shrink-0">
        <Button variant="outline" className="flex-1 text-[13px]" onClick={onClose}>{t.crm.confirmSheets.cancel}</Button>
        <Button
          className="flex-[1.4] text-[13px] gap-1.5"
          style={{ background: 'linear-gradient(135deg, #1B4332, #2D5A3D)' }}
          disabled={!selected}
          onClick={async () => {
            if (currentClub && selected) {
              await memberActionLogService.logAction({
                memberIds: [member.id],
                clubId: currentClub.id,
                actionType: 'comp_something',
                payload: { comp_type: selected },
              });
            }
            toast({ title: `Comped: ${selected}`, description: `Logged for ${member.name}` });
            onClose();
          }}
        >
          <LayoutGrid className="w-3.5 h-3.5" />
          {t.crm.confirmSheets.confirmComp}
        </Button>
      </div>
    </>
  );
};

// ---------------------------------------------------------------------------
// Snooze sheet — "Mark as handled" with 3/7/14 day duration picker
// ---------------------------------------------------------------------------

export const SnoozeSheet: React.FC<{
  member: ActionSheetMember;
  onClose: () => void;
}> = ({ member, onClose }) => {
  const { t } = useAppTranslations();
  const { currentClub } = useClubAdmin();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedDays, setSelectedDays] = useState<3 | 7 | 14>(7);
  const [isSaving, setIsSaving] = useState(false);

  const handleConfirm = async () => {
    if (!currentClub) return;
    setIsSaving(true);
    try {
      const snoozeUntil = new Date(Date.now() + selectedDays * 86_400_000).toISOString();
      await memberActionLogService.logAction({
        memberIds: [member.id],
        clubId: currentClub.id,
        actionType: 'snooze',
        payload: { snooze_until: snoozeUntil, duration_days: selectedDays },
      });
      queryClient.invalidateQueries({ queryKey: ['memberProgress'] });
      queryClient.invalidateQueries({ queryKey: ['member-action-timeline'] });
      toast({
        title: t.crm.confirmSheets.markedAsHandled,
        description: `${member.name} ${t.crm.confirmSheets.hiddenFromUrgent} ${selectedDays} ${t.crm.confirmSheets.days}`,
      });
      onClose();
    } catch {
      toast({ title: t.crm.confirmSheets.failedToSend, variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  const DURATIONS: Array<{ days: 3 | 7 | 14; label: string; sub: string }> = [
    { days: 3,  label: '3 days',  sub: 'Quick recheck'    },
    { days: 7,  label: '7 days',  sub: 'Standard window'  },
    { days: 14, label: '14 days', sub: 'Longer follow-up' },
  ];

  return (
    <>
      <div className="px-6 py-5 border-b border-slate-100 flex-shrink-0" style={{ paddingTop: 'calc(1.25rem + env(safe-area-inset-top, 0px))', paddingBottom: '1.25rem' }}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{t.crm.confirmSheets.markAsHandled}</p>
            <p className="text-[17px] font-bold text-slate-900 mt-1">{member.name}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
        <p className="text-[12px] text-slate-500 leading-relaxed">
          {t.crm.confirmSheets.snoozeBody}
        </p>

        <div>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">{t.crm.confirmSheets.recheckAfter}</p>
          <div className="space-y-2">
            {DURATIONS.map(opt => (
              <button
                key={opt.days}
                onClick={() => setSelectedDays(opt.days)}
                className={`w-full text-left px-4 py-3 rounded-xl border transition-colors ${
                  selectedDays === opt.days
                    ? 'bg-amber-50 border-amber-300'
                    : 'bg-white border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className={`text-[13px] font-semibold ${selectedDays === opt.days ? 'text-amber-800' : 'text-slate-700'}`}>
                  {opt.label}
                </div>
                <div className="text-[11px] text-slate-400 mt-0.5">{opt.sub}</div>
              </button>
            ))}
          </div>
        </div>

        <p className="text-[11px] text-slate-400 flex items-center gap-1.5 pt-2">
          <span className="w-3.5 h-3.5 rounded-full bg-slate-200 flex items-center justify-center text-slate-500 flex-shrink-0 text-[8px] font-bold">i</span>
          {t.crm.confirmSheets.returnsToRed}
        </p>
      </div>

      <div className="px-6 py-4 border-t border-slate-100 flex gap-2 flex-shrink-0">
        <Button variant="outline" className="flex-1 text-[13px]" onClick={onClose}>{t.crm.confirmSheets.cancel}</Button>
        <Button
          className="flex-[1.4] text-[13px] gap-1.5"
          style={{ background: 'linear-gradient(135deg, #D97706, #B45309)' }}
          onClick={handleConfirm}
          disabled={isSaving}
        >
          {isSaving ? t.crm.confirmSheets.saving : `Snooze ${selectedDays}d`}
        </Button>
      </div>
    </>
  );
};

// ---------------------------------------------------------------------------
// Unsnooze sheet — one-tap undo
// ---------------------------------------------------------------------------

export const UnsnoozeSheet: React.FC<{
  member: ActionSheetMember;
  onClose: () => void;
}> = ({ member, onClose }) => {
  const { t } = useAppTranslations();
  const { currentClub } = useClubAdmin();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isSaving, setIsSaving] = useState(false);

  const handleConfirm = async () => {
    if (!currentClub) return;
    setIsSaving(true);
    try {
      await memberActionLogService.logAction({
        memberIds: [member.id],
        clubId: currentClub.id,
        actionType: 'snooze',
        payload: { undo: true },
      });
      queryClient.invalidateQueries({ queryKey: ['memberProgress'] });
      queryClient.invalidateQueries({ queryKey: ['member-action-timeline'] });
      toast({ title: t.crm.confirmSheets.snoozeRemoved, description: `${member.name} ${t.crm.confirmSheets.backOnUrgentList}` });
      onClose();
    } catch {
      toast({ title: t.crm.confirmSheets.failedToUndo, variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <div className="px-6 py-5 border-b border-slate-100 flex-shrink-0" style={{ paddingTop: 'calc(1.25rem + env(safe-area-inset-top, 0px))', paddingBottom: '1.25rem' }}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{t.crm.confirmSheets.undoSnooze}</p>
            <p className="text-[17px] font-bold text-slate-900 mt-1">{member.name}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-5">
        <p className="text-[12px] text-slate-500 leading-relaxed">
          {t.crm.confirmSheets.undoSnoozeBody}
        </p>
      </div>

      <div className="px-6 py-4 border-t border-slate-100 flex gap-2 flex-shrink-0">
        <Button variant="outline" className="flex-1 text-[13px]" onClick={onClose}>{t.crm.confirmSheets.cancel}</Button>
        <Button
          className="flex-[1.4] text-[13px] gap-1.5"
          style={{ background: 'linear-gradient(135deg, #DC2626, #B91C1C)' }}
          onClick={handleConfirm}
          disabled={isSaving}
        >
          {isSaving ? t.crm.confirmSheets.saving : t.crm.confirmSheets.undoSnooze}
        </Button>
      </div>
    </>
  );
};

// Export sub-sheets so the new MemberDetailPanel can render them in its Send tab
export { ReEngagementSheet, LogFollowUpSheet, ReassignCoachSheet, CompSomethingSheet };

// ---------------------------------------------------------------------------
// Root sheet — switches on actionType
// ---------------------------------------------------------------------------

export const MemberActionConfirmSheet: React.FC<MemberActionConfirmSheetProps> = ({
  actionType,
  member,
  onClose,
}) => {
  if (!actionType || !member) return null;

  return createPortal(
    <>
      {/* Invisible backdrop */}
      <div className="fixed inset-0 z-[48]" onClick={onClose} />

      {/* Panel — z-[55] to sit above z-50 TopNav on mobile full-screen */}
      <div
        className="fixed right-0 sm:right-6 top-0 sm:top-6 bottom-0 sm:bottom-6 w-full sm:w-[400px] bg-white border-0 sm:border border-slate-200 rounded-none sm:rounded-2xl flex flex-col z-[55]"
        style={{ boxShadow: '-20px 0 60px rgba(15,23,42,0.12)' }}
        onClick={e => e.stopPropagation()}
      >
        {actionType === 're_engagement'  && <ReEngagementSheet  member={member} onClose={onClose} />}
        {actionType === 'reassign_coach' && <ReassignCoachSheet member={member} onClose={onClose} />}
        {actionType === 'comp_something' && <CompSomethingSheet member={member} onClose={onClose} />}
        {actionType === 'log_followup'   && <LogFollowUpSheet   member={member} onClose={onClose} />}
        {actionType === 'snooze'         && <SnoozeSheet        member={member} onClose={onClose} />}
        {actionType === 'unsnooze'       && <UnsnoozeSheet      member={member} onClose={onClose} />}
      </div>
    </>,
    document.body,
  );
};

// ---------------------------------------------------------------------------
// Bulk composer sheet — variant C (multi-select, 2+ members)
// ---------------------------------------------------------------------------

export interface BulkActionComposerProps {
  actionType: MemberActionType | null;
  members: ActionSheetMember[];
  onClose: () => void;
}

const BULK_TEMPLATES = RE_ENGAGEMENT_TEMPLATES;

export const BulkActionComposerSheet: React.FC<BulkActionComposerProps> = ({
  actionType,
  members,
  onClose,
}) => {
  const { t } = useAppTranslations();
  const { currentClub } = useClubAdmin();
  const { toast } = useToast();
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [note, setNote] = useState('');

  useEffect(() => {
    if (actionType === 're_engagement') {
      const tpl = BULK_TEMPLATES[selectedIdx];
      setSubject(tpl.subject('{{first_name}}'));
      setBody(tpl.body('{{first_name}}'));
    }
  }, [selectedIdx, actionType]);

  if (!actionType || members.length === 0) return null;

  const handleSendBulk = async () => {
    if (!currentClub) return;
    setIsSending(true);
    try {
      await Promise.all(
        members.map(m =>
          staffMemberMessagingService.sendMemberMessage({
            memberUserId: m.id,
            clubId: currentClub.id,
            content: `Subject: ${subject.replace('{{first_name}}', m.name.split(' ')[0])}\n\n${body.replace(/{{first_name}}/g, m.name.split(' ')[0])}`,
          })
        )
      );
      await memberActionLogService.logAction({
        memberIds: members.map(m => m.id),
        clubId: currentClub.id,
        actionType: 're_engagement',
        payload: { template: BULK_TEMPLATES[selectedIdx].label, bulk: true },
      });
      toast({ title: t.crm.confirmSheets.messagesSent, description: `${t.crm.confirmSheets.sentTo} ${members.length} members` });
      onClose();
    } catch {
      toast({ title: t.crm.confirmSheets.failedToSend, variant: 'destructive' });
    } finally {
      setIsSending(false);
    }
  };

  const handleSaveNote = async () => {
    if (!note.trim()) return;
    if (currentClub) {
      await memberActionLogService.logAction({
        memberIds: members.map(m => m.id),
        clubId: currentClub.id,
        actionType: 'log_followup',
        payload: { note, bulk: true },
      });
    }
    toast({ title: t.crm.confirmSheets.followUpLogged, description: `${t.crm.confirmSheets.savedFor} ${members.length} members` });
    onClose();
  };

  return createPortal(
    <>
      <div className="fixed inset-0 z-50" onClick={onClose} />

      <div
        className="fixed right-0 sm:right-6 top-0 sm:top-6 bottom-0 sm:bottom-6 w-full sm:w-[400px] bg-white border-0 sm:border border-slate-200 rounded-none sm:rounded-2xl flex flex-col z-[60]"
        style={{ boxShadow: '-20px 0 60px rgba(15,23,42,0.12)' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-5 border-b border-slate-100 flex-shrink-0" style={{ paddingTop: 'calc(1.25rem + env(safe-area-inset-top, 0px))', paddingBottom: '1.25rem' }}>
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{t.crm.confirmSheets.bulkSend}</p>
              <p className="text-[17px] font-bold text-slate-900 mt-1">
                {actionType === 're_engagement' && t.crm.actionMenu.sendReEngagement}
                {actionType === 'log_followup'   && t.crm.actionMenu.logFollowUp}
                {actionType === 'reassign_coach' && t.crm.actionMenu.reassignCoach}
                {actionType === 'comp_something' && t.crm.actionMenu.compSomething}
                {' '}· {members.length} members
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors"
            >
              <X className="w-4 h-4 text-slate-500" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

          {/* Recipients chips */}
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">{t.crm.confirmSheets.recipientsLabel}</p>
            <div className="flex flex-wrap gap-1.5">
              {members.map(m => (
                <span
                  key={m.id}
                  className="inline-flex items-center px-2.5 py-1 rounded-lg bg-slate-100 text-[12px] font-medium text-slate-700 border border-slate-200"
                >
                  {m.name.split(' ')[0]} {m.name.split(' ')[1]?.[0]}.
                </span>
              ))}
            </div>
          </div>

          {/* Re-engagement content */}
          {actionType === 're_engagement' && (
            <>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">{t.crm.confirmSheets.templateLabel}</p>
                <div className="flex gap-2 flex-wrap">
                  {BULK_TEMPLATES.map((tpl, idx) => (
                    <button
                      key={tpl.label}
                      onClick={() => setSelectedIdx(idx)}
                      className={`px-3 py-1.5 rounded-lg text-[12px] font-semibold border transition-colors ${
                        selectedIdx === idx
                          ? 'bg-green-50 text-green-700 border-green-300'
                          : 'bg-slate-50 text-slate-600 border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      {tpl.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">{t.crm.confirmSheets.subjectLabel}</p>
                <input
                  type="text"
                  value={subject}
                  onChange={e => setSubject(e.target.value)}
                  className="w-full px-3 py-2 text-[13px] border border-slate-200 rounded-lg focus:outline-none focus:border-slate-400 text-slate-800"
                />
              </div>

              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">{t.crm.confirmSheets.messagePreviewLabel}</p>
                <div className="px-4 py-3.5 bg-slate-50 rounded-xl text-[13px] text-slate-700 leading-relaxed whitespace-pre-line">
                  {body.replace(/{{first_name}}/g, '{{first_name}}')}
                </div>
                <p className="text-[11px] text-slate-400 mt-1.5">
                  <span className="bg-amber-100 text-amber-700 px-1 py-0.5 rounded text-[10px] font-mono">{'{{first_name}}'}</span>
                  {' '}{t.crm.confirmSheets.personalised}
                </p>
              </div>
            </>
          )}

          {/* Log follow-up content */}
          {actionType === 'log_followup' && (
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">{t.crm.confirmSheets.noteLabel}</p>
              <textarea
                value={note}
                onChange={e => setNote(e.target.value)}
                placeholder="What did you do or discuss? E.g. 'Called, left voicemail. Will try again Thursday.'"
                rows={6}
                className="w-full px-3 py-2.5 text-[13px] border border-slate-200 rounded-lg focus:outline-none focus:border-slate-400 text-slate-800 resize-none leading-relaxed placeholder:text-slate-300"
              />
              <p className="text-[11px] text-slate-400 mt-1.5">{t.crm.confirmSheets.actionLoggedEachProfile}</p>
            </div>
          )}

          {/* Reassign / Comp stubs */}
          {(actionType === 'reassign_coach' || actionType === 'comp_something') && (
            <p className="text-[12px] text-slate-400 text-center py-8">
              {actionType === 'reassign_coach' ? t.crm.confirmSheets.coachPickerComingSoon : t.crm.confirmSheets.voucherSystemComingSoon}
            </p>
          )}

          <p className="text-[11px] text-slate-400 flex items-center gap-1.5">
            <span className="w-3.5 h-3.5 rounded-full bg-slate-200 flex items-center justify-center text-slate-500 flex-shrink-0 text-[8px] font-bold">i</span>
            {t.crm.confirmSheets.actionLoggedEachProfile}
          </p>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 flex gap-2 flex-shrink-0">
          <Button variant="outline" className="flex-1 text-[13px]" onClick={onClose}>{t.crm.confirmSheets.cancel}</Button>
          {actionType === 're_engagement' && (
            <Button
              className="flex-[1.4] text-[13px] gap-1.5"
              style={{ background: 'linear-gradient(135deg, #2A4494, #1E3270)' }}
              onClick={handleSendBulk}
              disabled={isSending}
            >
              <Send className="w-3.5 h-3.5" />
              {isSending ? t.crm.confirmSheets.sending : `${t.crm.confirmSheets.sendNow} (${members.length})`}
            </Button>
          )}
          {actionType === 'log_followup' && (
            <Button
              className="flex-[1.4] text-[13px] gap-1.5"
              style={{ background: 'linear-gradient(135deg, #1B4332, #2D5A3D)' }}
              onClick={handleSaveNote}
              disabled={!note.trim()}
            >
              <PenLine className="w-3.5 h-3.5" />
              {t.crm.confirmSheets.saveNote}
            </Button>
          )}
          {(actionType === 'reassign_coach' || actionType === 'comp_something') && (
            <Button
              className="flex-[1.4] text-[13px] gap-1.5"
              style={{ background: 'linear-gradient(135deg, #2A4494, #1E3270)' }}
              onClick={() => { toast({ title: t.crm.confirmSheets.comingSoon }); onClose(); }}
            >
              <UserPlus className="w-3.5 h-3.5" />
              {t.crm.confirmSheets.confirm}
            </Button>
          )}
        </div>
      </div>
    </>,
    document.body,
  );
};
